/**
 * /medical-tourism-support-vietnam
 *
 * Single SEO landing page — Medical Tourism Support in Vietnam.
 * Reuses existing site components. No new design work.
 *
 * Copy safety:
 *   • Independent platform, not a hospital/clinic.
 *   • No diagnosis, no result guarantees.
 *   • Never claims Vietnam is "the best" medical tourism destination.
 *   • No fixed savings claims; no 24/7 promises; no fixed response times.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ShieldCheck, MessageCircle, Plane, ClipboardCheck,
  AlertTriangle, CheckCircle2, Hotel, Heart,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentIntro, UrgentCardGrid, UrgentSteps, UrgentFaq, UrgentEditorial,
  UrgentCtaBand,
} from '../components/UrgentCareSections';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const SITE = 'https://vietclinics.com';
const SLUG = '/medical-tourism-support-vietnam';

const HERO_IMAGE =
  'https://images.pexels.com/photos/7088530/pexels-photo-7088530.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK =
  'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';

const WHAT_IS_SUPPORT = [
  { title: 'Understand treatment options',     body: 'We help you understand what is realistic for your case, what alternatives exist, and what good care in Vietnam looks like for your specific treatment.' },
  { title: 'Connect with suitable providers',   body: 'We share licensed doctor, clinic and hospital options from our verified partner network — with credentials and language notes up front.' },
  { title: 'Online consultations before travel', body: 'Where possible we arrange a video consultation so you can meet the doctor and ask questions before booking any flights.' },
  { title: 'Coordinate appointment dates',      body: 'We align your appointments, scans and follow-ups so your trip is efficient — and so recovery has the time it needs.' },
  { title: 'Translator & communication support', body: 'English-speaking coordination by default, with Vietnamese translator help where available and useful for your case.' },
  { title: 'Treatment timeline guidance',        body: 'We explain realistic timelines for surgery, dental work, hair transplant, eye treatment and checkups — and how long to stay in Vietnam.' },
  { title: 'Travel, hotel & local guidance',    body: 'Where useful we share guidance on hotel zones, transport and local logistics around your appointments.' },
  { title: 'Follow-up & aftercare',              body: 'After treatment, we help with check-ins, prescription guidance, telehealth follow-ups and travel insurance documentation.' },
];

const HOW_WE_SUPPORT = [
  { title: 'Treatment enquiry review',           body: 'A short enquiry — your treatment interest, city, timing and budget — reviewed by a real coordinator (no payment to ask).' },
  { title: 'Doctor & clinic recommendations',    body: 'You receive credentialed doctor and clinic options matched to your case, with indicative pricing.' },
  { title: 'Online consultation coordination',   body: 'We arrange a video consultation where useful — meet the doctor, ask questions, then decide on travel.' },
  { title: 'Translator & communication support', body: 'English-speaking coordination plus Vietnamese translator help where available — for appointments, hospital paperwork and aftercare.' },
  { title: 'Treatment plan & quote explanation', body: 'We help you read the written treatment plan and indicative quote in plain English — what is included, recovery time, follow-up.' },
  { title: 'Travel date & appointment timing',   body: 'We align consultations, treatment dates and recovery windows so your trip works end-to-end.' },
  { title: 'Hotel & airport guidance',           body: 'Where helpful, we share hotel-zone suggestions near your clinic and arrival/transport guidance.' },
  { title: 'Follow-up & aftercare',              body: 'After treatment, we coordinate follow-up reviews, telehealth check-ins and documentation for insurance claims.' },
];

const TREATMENT_BLOCKS = [
  { to: '/dental-treatments-in-vietnam',          label: 'Dental treatment & implants in Vietnam',  sub: 'Implants, veneers, crowns, full-mouth restoration — transparent USD pricing' },
  { to: '/cosmetic-surgery-in-vietnam',           label: 'Cosmetic & plastic surgery in Vietnam',   sub: 'Korean & US-trained surgeons at JCI-accredited hospitals' },
  { to: '/hair-restoration-in-vietnam',           label: 'Hair transplant in Vietnam',              sub: 'Sapphire FUE & DHI — natural hairline design' },
  { to: '/eye-care-treatments-in-vietnam',        label: 'Eye treatment & vision correction',       sub: 'LASIK, cataract, glaucoma and retina specialists' },
  { to: '/preventive-care-treatments-in-vietnam', label: 'Health checkups & specialist consults',   sub: 'Executive health and specialist screenings' },
  { to: '/urgent-medical-care',                   label: 'Urgent care for travelers already in Vietnam', sub: 'Hotel doctor, IV therapy & English-speaking telehealth' },
];

const PREP_CHECKLIST = [
  'Treatment goals — what outcome you want',
  'Medical history',
  'Current medications',
  'Allergies (medication, food, latex, anaesthesia)',
  'Previous surgery or treatment relevant to your case',
  'Photos, X-rays, scans or recent medical reports',
  'Preferred city in Vietnam',
  'Indicative budget range',
  'Possible travel dates',
  'How much recovery time you can plan for',
  'Questions you want to ask the doctor',
  'Companion or support needs during your stay',
];

const SUPPORT_JOURNEY = [
  { title: 'Initial enquiry',                body: 'A short WhatsApp, email or chat message describing your treatment interest, city and timing.' },
  { title: 'Basic case review',              body: 'Our coordinator reviews your case and identifies suitable doctor and clinic options.' },
  { title: 'Doctor / clinic options',         body: 'You receive credentialed options with experience notes, language and indicative pricing.' },
  { title: 'Online consultation',             body: 'Where useful, a video consultation so you can meet the doctor before any travel commitment.' },
  { title: 'Treatment planning',              body: 'A written treatment plan in plain English — diagnosis context, procedure, recovery, price.' },
  { title: 'Travel planning',                 body: 'We align travel dates, hotel guidance and appointment timing around the medical schedule.' },
  { title: 'Appointment coordination',        body: 'Confirmations, reminders and on-the-ground support during your treatment visit.' },
  { title: 'Follow-up & aftercare',           body: 'Telehealth check-ins, prescription guidance and travel insurance documentation post-treatment.' },
];

const WHO_ITS_FOR = [
  'International patients planning treatment in Vietnam',
  'Medical tourists comparing options across countries',
  'Expats living in Vietnam',
  'Travelers needing healthcare during their trip',
  'Patients comparing treatment prices overseas',
  'Patients considering cosmetic or plastic surgery',
  'Patients considering dental treatment',
  'Patients considering hair transplant',
  'Patients considering eye treatment',
  'Patients arranging executive health checkups',
  'Patients seeking a second opinion before travelling',
];

const CITIES = [
  { name: 'Ho Chi Minh City', region: 'Southern Vietnam' },
  { name: 'Hanoi',            region: 'Northern Vietnam' },
  { name: 'Da Nang',          region: 'Central Coast' },
  { name: 'Hoi An',           region: 'Central Coast' },
  { name: 'Vung Tau',         region: 'Southern Coast' },
  { name: 'Nha Trang',        region: 'South Central Coast' },
  { name: 'Phu Quoc',         region: 'Island' },
];

const FAQ = [
  { q: 'What is medical tourism support in Vietnam?',                  a: 'Medical tourism support helps international patients plan, travel for and recover from medical treatment in Vietnam. With VietClinics, this means doctor and clinic recommendations, online consultations before travel, treatment plan explanation, translator and communication support, travel and appointment coordination, and follow-up aftercare — all coordinated through a single English-speaking concierge contact.' },
  { q: 'Can VietClinics help me plan treatment in Vietnam?',           a: 'Yes. We help you understand your options, share suitable doctor and clinic recommendations, arrange consultations (online or in person), coordinate appointment timing around your travel, and support follow-up after treatment. We never claim Vietnam is "the best" medical tourism destination — we help you decide whether it is right for your case.' },
  { q: 'Is VietClinics a clinic or hospital?',                          a: 'No. VietClinics is an independent healthcare coordination platform. We do not provide medical advice, diagnosis or treatment ourselves. All medical advice and treatment decisions come from licensed healthcare professionals at our partner clinics and hospitals.' },
  { q: 'Can VietClinics recommend doctors or clinics?',                a: 'Yes. We share doctor and clinic options from our verified partner network with credentials, experience notes and language information. See our Doctor Recommendation in Vietnam page for the full framework we use.' },
  { q: 'Can VietClinics arrange an online consultation before I travel?', a: 'Yes, where available. A video consultation lets you meet the doctor, ask questions and review the proposed plan before committing to flights or accommodation — often the most efficient way to start.' },
  { q: 'Can VietClinics help with medical translator support?',         a: 'Yes — translator and English-speaking communication support is available where confirmed for your specific city, appointment and provider. See our Medical Translator & Communication Support page for details.' },
  { q: 'Can VietClinics help with dental treatment in Vietnam?',        a: 'Yes — see our Dental Treatment in Vietnam hub. We coordinate with verified dental clinics for implants, veneers, crowns and full-mouth restoration with transparent pricing and English-speaking support.' },
  { q: 'Can VietClinics help with cosmetic surgery in Vietnam?',        a: 'Yes — see our Cosmetic Surgery in Vietnam hub. We coordinate with verified plastic surgeons at JCI-accredited hospital-affiliated centres, with full surgeon credentials shared before any consultation.' },
  { q: 'Can VietClinics help with airport, hotel, or appointment coordination?', a: 'Where helpful, we share hotel-zone guidance near your clinic, transport information and appointment confirmation/reminders. The level of travel coordination depends on your city, treatment and timing — we confirm what is realistic for your case before you commit.' },
  { q: 'Does VietClinics provide medical advice?',                       a: 'No. VietClinics does not provide medical advice, diagnosis or treatment recommendations. All medical decisions must come from licensed healthcare professionals at our partner clinics and hospitals.' },
];

const RELATED_LINKS = [
  { to: '/patient-support-vietnam',        label: 'Patient Support in Vietnam',                  sub: 'Coordination, translator help and aftercare' },
  { to: '/care-after-discharge-vietnam',   label: 'Care After Hospital Discharge in Vietnam',    sub: 'Hotel room recovery support, nurse visits & follow-up coordination' },
  { to: '/doctor-recommendation-vietnam',  label: 'Doctor Recommendation in Vietnam',            sub: 'Find suitable licensed doctors, clinics & specialists' },
  { to: '/medical-translator-vietnam',     label: 'Medical Translator & Communication Support',  sub: 'Appointment translation, question prep & treatment plan explanation' },
  { to: '/how-to-choose-doctor-vietnam',   label: 'How to Choose a Doctor in Vietnam',           sub: 'Comparison criteria, red flags, questions to ask' },
  { to: '/how-it-works',                   label: 'How VietClinics Works',                       sub: 'The 4-step concierge process explained' },
  { to: '/contact',                        label: 'Contact VietClinics',                         sub: 'Talk to a coordinator on WhatsApp or email' },
  { to: '/cosmetic-surgery-in-vietnam',    label: 'Cosmetic Surgery in Vietnam',                 sub: 'Verified plastic surgeons at JCI-accredited hospitals' },
  { to: '/dental-treatments-in-vietnam',   label: 'Dental Treatment in Vietnam',                 sub: 'Implants, veneers and full-mouth restoration' },
  { to: '/hair-restoration-in-vietnam',    label: 'Hair Transplant in Vietnam',                  sub: 'Sapphire FUE & DHI by Korean & Turkish-trained surgeons' },
  { to: '/eye-care-treatments-in-vietnam', label: 'Eye Treatment in Vietnam',                    sub: 'LASIK, cataract and retina specialists' },
  { to: '/urgent-medical-care',            label: 'Urgent Medical Care in Vietnam',              sub: 'For non-emergency urgent care while travelling' },
  { to: '/preventive-care-treatments-in-vietnam', label: 'Health Checkups in Vietnam',           sub: 'Executive health and specialist screenings' },
];

const EDITORIAL = {
  reviewedBy: 'VietClinics Patient Concierge Team',
  role: 'Medical travel & treatment coordinators',
  methodology:
    'Medical tourism support is delivered through our verified partner network of licensed doctors, clinics and hospital-affiliated surgical centres. Partners are vetted against a 32-point internal checklist covering qualifications, facility standards, language support, transparent pricing and aftercare. Coverage and travel logistics are confirmed per case. Reviewed quarterly.',
  lastUpdated: '2026-02-15',
};

const MedicalTourismSupportVietnamPage = () => {
  const { contactInfo } = useContactInfo();
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'medical-tourism-support', HERO_IMAGE);
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics, I am planning medical treatment in Vietnam and would like support.'
  );
  const canonical = `${SITE}${SLUG}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'Medical Tourism Support & Patient Coordination',
        provider: { '@type': 'Organization', name: 'VietClinics' },
        areaServed: [{ '@type': 'Country', name: 'Vietnam' }],
        url: canonical,
        description:
          'Independent medical tourism support for international patients planning treatment in Vietnam — doctor recommendations, online consultations, translator support, treatment planning, travel coordination and aftercare.',
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'Medical Tourism Support in Vietnam',
        description:
          'VietClinics helps international patients plan medical travel to Vietnam with doctor recommendations, clinic coordination, translator support, treatment planning, travel guidance, and aftercare support.',
        inLanguage: 'en',
        dateModified: EDITORIAL.lastUpdated,
        lastReviewed: EDITORIAL.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: EDITORIAL.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Patient Support', item: `${SITE}/patient-support-vietnam` },
          { '@type': 'ListItem', position: 3, name: 'Medical Tourism Support', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Medical Tourism Support in Vietnam | Treatment, Travel &amp; Patient Coordination | VietClinics</title>
        <meta
          name="description"
          content="VietClinics helps international patients plan medical travel to Vietnam with doctor recommendations, clinic coordination, translator support, treatment planning, travel guidance, and aftercare support."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Medical Tourism Support in Vietnam | VietClinics" />
        <meta
          property="og:description"
          content="Independent medical travel coordination for international patients — doctors, clinics, translator support, online consultations, treatment planning and aftercare in Vietnam."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <section className="relative overflow-hidden bg-white" data-testid="med-tourism-hero">
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)' }} />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                Medical tourism support · Vietnam
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                Medical Tourism Support in Vietnam
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                Independent treatment, travel and patient coordination support for
                international patients planning healthcare, dental treatment, cosmetic
                surgery, hair transplant, eye treatment, health checkups or specialist
                care in Vietnam.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {whatsappHref && (
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold rounded-full px-6 py-3.5 transition-colors" data-testid="med-tourism-cta-plan">
                    <MessageCircle className="h-4 w-4" />
                    Plan Medical Treatment in Vietnam
                  </a>
                )}
                <Link to="/patient-support-vietnam" className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-[#1FA7A1] font-bold rounded-full px-6 py-3.5 transition-colors" data-testid="med-tourism-cta-support">
                  Request Patient Support
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden bg-slate-100" style={{ aspectRatio: '4 / 3' }}>
              <img src={heroImage} onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }} alt="Medical tourism support in Vietnam for international patients" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-100" data-testid="med-tourism-trust">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck,    label: 'Verified partner network' },
              { icon: Plane,          label: 'Built for international patients' },
              { icon: Hotel,          label: 'Travel & appointment coordination' },
              { icon: Heart,          label: 'Independent — not a hospital' },
            ].map(({ icon: Icon, label }, i) => (
              <div key={label} className="flex items-center gap-3" data-testid={`med-tourism-trust-${i + 1}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}><Icon className="h-4 w-4 text-[#1A8F8A]" /></div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <UrgentIntro
        heading="Travelling for treatment shouldn't feel like figuring it all out alone."
        paragraphs={[
          'Travelling to another country for healthcare can feel confusing. You need to choose a doctor, compare clinics, understand prices, plan flights and recovery time, communicate clearly with providers, and have someone reachable if anything changes. Doing all of that from abroad — often in a second language — is genuinely hard.',
          'VietClinics is an independent healthcare coordination platform built around that exact challenge. We help international patients understand their options for treatment in Vietnam, connect with suitable licensed doctors and clinics, arrange consultations before travel, coordinate appointment timing, and support communication and aftercare throughout your journey.',
          'We are not a hospital or clinic, and we do not provide medical diagnosis or guarantee outcomes. We also do not claim Vietnam is "the best" medical tourism destination — that is a personal decision. What we do is make the process clearer so you can decide with confidence whether Vietnam is right for your case.',
        ]}
        testId="med-tourism-intro"
      />

      <UrgentCardGrid eyebrow="What is medical tourism support?" heading="Eight things we actually help with" items={WHAT_IS_SUPPORT} testId="med-tourism-what-is" />

      <UrgentCardGrid eyebrow="How VietClinics supports medical travel" heading="From first enquiry to follow-up — one concierge contact" items={HOW_WE_SUPPORT} testId="med-tourism-how" />

      <section className="py-16 lg:py-20 bg-white" data-testid="med-tourism-treatments">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Treatments patients travel to Vietnam for</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">Most-requested treatments by international patients</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TREATMENT_BLOCKS.map((b) => (
              <Link key={b.to} to={b.to} className="group bg-white hover:bg-[#F0FAF8] border border-slate-100 hover:border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4" style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }} data-testid={`med-tourism-treatment-${b.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}>
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{b.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{b.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-tourism-prep">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Planning before you travel</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">12-point planning checklist</h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {PREP_CHECKLIST.map((item, i) => (
              <li key={i} className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4" style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }} data-testid={`med-tourism-prep-${i + 1}`}>
                <ClipboardCheck className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white" data-testid="med-tourism-online-consultation">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Online consultation before travelling</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">Meet the doctor — before you book flights</h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>Many patients prefer to speak with the doctor before booking flights or accommodation. VietClinics helps coordinate online video consultations where available, prepare your questions in advance, share basic case information with the doctor so the call is efficient, and stay reachable afterwards to clarify anything.</p>
            <p>An online doctor consultation in Vietnam is often the fastest, lowest-commitment way to understand your treatment options, get a realistic timeline, and decide whether medical travel is the right next step for your case.</p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-tourism-translator">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Translator &amp; communication support</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">Clear communication is part of safe medical travel</h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>Clear communication helps reduce misunderstandings — about symptoms, medical history, allergies, treatment goals, written plans and aftercare. VietClinics coordinates English-speaking patient support by default, and Vietnamese translator help where available and useful for your specific appointment.</p>
            <p>For a detailed view of what is included, see <Link to="/medical-translator-vietnam" className="text-[#1FA7A1] font-bold hover:underline">Medical Translator &amp; Communication Support in Vietnam</Link>.</p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white" data-testid="med-tourism-choosing">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Choosing doctors, clinics &amp; hospitals</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">What to weigh when comparing options</h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>When travelling for treatment, consider doctor experience with your specific case, license and qualifications, the standards of the clinic or hospital, international patient experience, communication quality, written treatment planning, aftercare and revision policies, transparent pricing, and how convenient the location is for your travel plan.</p>
            <p>See <Link to="/doctor-recommendation-vietnam" className="text-[#1FA7A1] font-bold hover:underline">Doctor Recommendation in Vietnam</Link> and <Link to="/how-to-choose-doctor-vietnam" className="text-[#1FA7A1] font-bold hover:underline">How to Choose a Doctor in Vietnam</Link> for the full comparison framework.</p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-tourism-travel-coord">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Travel &amp; local coordination</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">Practical support on the ground</h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed mb-6">
            <p>Depending on the case and city, VietClinics may help guide patients with airport arrival information, hotel-zone suggestions near your clinic, transport to appointments, appointment reminders, local support during your stay, translator coordination, follow-up appointments and urgent care signposting where available.</p>
            <p>We do not promise 24/7 coverage or fixed response times — every case is confirmed individually so expectations are realistic.</p>
          </div>
        </div>
      </section>

      <UrgentSteps eyebrow="Support before, during &amp; after treatment" heading="The VietClinics medical travel journey" steps={SUPPORT_JOURNEY} testId="med-tourism-journey" />

      <section className="py-16 lg:py-20 bg-white" data-testid="med-tourism-who">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Who this page is for</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">We support patients with very different needs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WHO_ITS_FOR.map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#F0FAF8] border border-[#DCEFEC] rounded-2xl p-4" data-testid={`med-tourism-who-${i + 1}`}>
                <CheckCircle2 className="h-5 w-5 text-[#1A8F8A] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-tourism-cities">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Cities covered</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">Medical tourism support across Vietnam</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CITIES.map((c) => (
              <div key={c.name} className="bg-white border border-slate-100 rounded-2xl px-4 py-3" data-testid={`med-tourism-city-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="font-extrabold text-slate-900 text-sm">{c.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{c.region}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-5 max-w-2xl">Coverage and travel coordination vary by city and treatment. We confirm what is realistic for your case before any commitment.</p>
        </div>
      </section>

      <UrgentFaq heading="Medical tourism support in Vietnam — frequently asked" items={FAQ} testId="med-tourism-faq" />

      <section className="py-16 lg:py-20 bg-white" data-testid="med-tourism-related">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Continue exploring VietClinics</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">More patient support &amp; treatment guides</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RELATED_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className="group bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4" data-testid={`med-tourism-related-${link.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}>
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{link.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{link.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <UrgentEditorial editorial={EDITORIAL} testId="med-tourism-editorial" />

      <section className="py-10 bg-white" data-testid="med-tourism-disclaimer-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-amber-50/60 border border-amber-100 rounded-2xl p-5 lg:p-6 flex gap-3" data-testid="med-tourism-disclaimer">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed text-amber-900">
              <p className="font-bold mb-1">Important notice</p>
              <p>VietClinics is an independent healthcare coordination platform. VietClinics is <strong>not a hospital or clinic</strong> and does <strong>not provide medical diagnosis</strong>. All medical advice, diagnosis and treatment decisions must come from licensed healthcare professionals. For life-threatening emergencies in Vietnam, dial <strong>115</strong>.</p>
            </div>
          </div>
        </div>
      </section>

      <UrgentCtaBand heading="Planning Medical Treatment in Vietnam?" subheading="Tell VietClinics what treatment you are considering, your preferred city and your travel plans. Our team can help you understand your options, arrange suitable consultations and coordinate your next steps." testId="med-tourism-final-cta" />

      <Footer />
    </div>
  );
};

export default MedicalTourismSupportVietnamPage;
